import React, { useEffect, useRef, useState } from 'react';
import { Card } from './ui/card';
import { useLanguage } from '../i18n';

const Gallery = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Villa Gazzani images
  const images = [
    {
      src: "https://customer-assets.emergentagent.com/job_civitanova-marriage/artifacts/dgh8rv42_villa_1.jpg",
      alt: t('gallery.alt1'),
      className: "col-span-1 row-span-1"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_civitanova-marriage/artifacts/t3uo9kf3_villagazzani3.jpg",
      alt: t('gallery.alt2'),
      className: "col-span-1 row-span-1"
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-28 md:py-40 bg-regency relative overflow-hidden"
      id="gallery"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 damask-pattern opacity-35" />
      <div className="absolute top-0 right-0 w-72 h-72 bg-lavender/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-blush/25 rounded-full blur-3xl" />
      
      {/* Floating sparkles */}
      <div className="absolute top-1/4 right-1/4 text-gold/30 text-xl animate-twinkle">✦</div>
      <div className="absolute bottom-1/4 left-1/4 text-gold/25 text-lg animate-twinkle delay-300">✦</div>
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-24 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="flex items-center justify-center mb-8">
            <svg className="w-16 h-16 text-lavender/40" viewBox="0 0 64 64" fill="currentColor">
              <path d="M32 10 C26 18 14 18 8 24 C14 28 26 28 32 38 C38 28 50 28 56 24 C50 18 38 18 32 10" />
              <circle cx="32" cy="24" r="4" fill="hsl(1 81% 35% / 0.5)" />
            </svg>
          </div>
          <p className="font-display text-sm uppercase tracking-[0.5em] text-gold mb-5">
            {t('gallery.eyebrow')}
          </p>
          <h2 className="font-script text-5xl md:text-6xl lg:text-7xl text-lavender mb-8">
            Villa Gazzani
          </h2>
          <p className="font-elegant text-xl md:text-2xl text-muted-foreground italic max-w-xl mx-auto">
            {t('gallery.subtitle')}
          </p>
          <div className="flex items-center justify-center gap-5 mt-10">
            <span className="w-20 h-px bg-gradient-to-r from-transparent to-gold/50" />
            <span className="text-lavender/50">❦</span>
            <span className="w-20 h-px bg-gradient-to-l from-transparent to-gold/50" />
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-7 max-w-5xl mx-auto">
          {images.map((image, index) => (
            <Card 
              key={index}
              className={`overflow-hidden border-2 border-lavender/15 shadow-romantic group ${image.className} transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${200 + index * 150}ms` }}
            >
              <div className="relative w-full h-full min-h-[240px] md:min-h-[300px] overflow-hidden rounded-lg">
                <img 
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* Elegant overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-lavender/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Decorative frame on hover */}
                <div className="absolute inset-4 border-2 border-gold/0 group-hover:border-gold/40 transition-all duration-500 rounded pointer-events-none" />
                
                {/* Corner decorations on hover */}
                <div className="absolute top-6 left-6 text-gold/0 group-hover:text-gold/50 transition-all duration-500 text-lg">✿</div>
                <div className="absolute bottom-6 right-6 text-gold/0 group-hover:text-gold/50 transition-all duration-500 text-lg">✿</div>
              </div>
            </Card>
          ))}
        </div>

        {/* Decorative element */}
        <div className={`flex items-center justify-center gap-5 mt-24 transition-all duration-1000 delay-700 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}>
          <span className="w-24 h-px bg-gradient-to-r from-transparent via-lavender/30 to-transparent" />
          <div className="flex items-center gap-2">
            <span className="text-gold/50 text-sm">✿</span>
            <span className="text-lavender/50 text-lg">❦</span>
            <span className="text-gold/50 text-sm">✿</span>
          </div>
          <span className="w-24 h-px bg-gradient-to-l from-transparent via-lavender/30 to-transparent" />
        </div>
        
        {/* Caption */}
        <p className={`text-center font-elegant text-xl text-muted-foreground italic mt-8 transition-all duration-1000 delay-800 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}>
          {t('gallery.caption')}
        </p>
      </div>
    </section>
  );
};

export default Gallery;
